import React from 'react'
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Wrench } from 'lucide-react'
import { Button, EmptyState, ErrorState, LoadingState, NavTabBar, Separator } from '@asad/crane-ui'
import type { NavTabItem } from '@asad/crane-ui'

import type { GetMQTTResponse } from '@/api/mqtt.api'
import type { TemplateForDeviceResponse } from '@/api/template.api'
import { useDeviceDataContext } from '@/context/DevicesContext'
import { toDeviceStatus } from '@/features/dashboard/adapters/device-status.adapter'
import { asTypedConfig } from '@/features/wizard-page/adapters/templateConfig.adapter'
import { DevicePowerChip } from '@/features/wizard-page/components/DevicePowerIndicator'
import { devicePowerStateOf } from '@/features/wizard-page/utils/devicePower'
import useDeviceLiveHistory from '@/features/device/hooks/useDeviceLiveHistory'
import { DeviceTypeIcon } from '@/features/dashboard/components/DeviceTypeIcon'
import { StatusChip } from '@/features/dashboard/components/StatusChip'
import { alertStatusOf } from '@/features/dashboard/config/statusConfig'
import { useDeviceSSE } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import useDeviceMeasurementsHistory from '@/hooks/useDeviceMeasurementsHistory'
import { getDeviceData } from '@/store/slices/device-slice'
import { getTemplateForDeviceData } from '@/store/slices/templates-slice'
import type { DeviceStatusType, DeviceTelemetryFreshness } from '@/features/dashboard/types'
import { getAllDevicesFromProfile, getTemplateByLanguage } from '@/utils/utils'

// Backend odpowiada tym samym 404 na urządzenie nieistniejące i niedostępne — ponowienie niczego nie zmieni.
const DEVICE_NOT_FOUND_ERROR = 'beNotifications.errors.deviceNotFound'

const withDeviceId = (ids: ReadonlySet<string>, id: string, present: boolean): ReadonlySet<string> => {
  if (ids.has(id) === present) return ids
  const next = new Set(ids)
  if (present) next.add(id)
  else next.delete(id)
  return next
}

const DeviceLayout = () => {
  const { id } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const device = useAppSelector((state) => (id ? state.device.devicePanelData[id] : undefined))
  const allTemplatesData = useAppSelector((state) => state.templates.templateForDeviceData)
  const profile = useAppSelector((state) => state.profile.data)
  const profileStatus = useAppSelector((state) => state.profile.status)
  const deviceLoadStatus = useAppSelector((state) => (id ? state.device.devicePanelStatusById[id] ?? 'idle' : 'idle'))
  const templateLoadStatus = useAppSelector((state) => state.templates.templateForDeviceStatus)

  const { t, i18n } = useTranslation()
  const selectedLanguage = i18n.language.toUpperCase()

  const { devicesStatuses, telemetryBySerial } = useDeviceDataContext()
  const fallbackStatus = React.useMemo(() => devicesStatuses.find((status) => status.id === id), [devicesStatuses, id])

  const selectedTemplate = React.useMemo<TemplateForDeviceResponse | undefined>(() => {
    if (!device?.deviceTemplates) return
    return getTemplateByLanguage(device.deviceTemplates, allTemplatesData, selectedLanguage)
  }, [device?.deviceTemplates, allTemplatesData, selectedLanguage])

  const canAccessAllDevices = profile?.role?.includes('ADMIN')
  // Zgłoszenie serwisu wysyła tylko klient — administrator ogląda urządzenie bez tej akcji.
  const canRequestService = Boolean(profile?.role?.includes('USER'))
  // Zakres urządzeń kierownika i serwisanta sprawdza backend (`assertCanReadDevice`); profil ich nie zawiera.
  const isServiceStaff = Boolean(
    profile?.role?.includes('SERVICE_MANAGER') || profile?.role?.includes('SERVICE_TECHNICIAN'),
  )
  const isServiceStaffOnly = isServiceStaff && !canAccessAllDevices && !canRequestService

  const isAssignedDevice = React.useMemo(() => {
    if (!id || !profile || isServiceStaffOnly) return false

    return getAllDevicesFromProfile(profile).some((device) => device.id === id)
  }, [id, isServiceStaffOnly, profile])

  const canAccessDevice = Boolean(id && profile && (canAccessAllDevices || isServiceStaff || isAssignedDevice))
  const deviceIdForRequests = canAccessDevice ? id : undefined
  const requestedTemplatesRef = React.useRef<Set<string>>(new Set())
  const [failedTemplateRequestKey, setFailedTemplateRequestKey] = React.useState<string | null>(null)
  const [unavailableDeviceIds, setUnavailableDeviceIds] = React.useState<ReadonlySet<string>>(() => new Set())
  const [retriedDeviceId, setRetriedDeviceId] = React.useState<string | null>(null)
  const [verifiedDeviceIds, setVerifiedDeviceIds] = React.useState<ReadonlySet<string>>(() => new Set())
  const currentTemplateRequestKey = device?.id ? `${device.id}:${selectedLanguage}` : null

  const isPanelRoute = pathname === `/device/${id}`
  const { deviceMeasurementsHistory } = useDeviceMeasurementsHistory(
    deviceIdForRequests,
    selectedTemplate?.id,
    isPanelRoute,
  )

  const serialNumber = device?.serialNumber
  const externalDeviceIds = React.useMemo(
    () => (canAccessDevice && !isAssignedDevice && serialNumber ? [serialNumber] : []),
    [canAccessDevice, isAssignedDevice, serialNumber],
  )
  const externalTelemetryBySerial = useDeviceSSE({ deviceIds: externalDeviceIds })
  const telemetry = serialNumber
    ? telemetryBySerial[serialNumber] ?? externalTelemetryBySerial[serialNumber]
    : undefined
  const telemetryFreshness: DeviceTelemetryFreshness = telemetry?.freshness ?? 'never'

  const mqtt = React.useMemo<GetMQTTResponse[]>(() => {
    return telemetry?.frames.length ? telemetry.frames : deviceMeasurementsHistory ?? []
  }, [deviceMeasurementsHistory, telemetry])

  const deviceStatus = React.useMemo<DeviceStatusType | undefined>(() => {
    if (!id) return fallbackStatus
    return toDeviceStatus({
      id,
      telemetry,
      baseline: fallbackStatus,
    })
  }, [fallbackStatus, id, telemetry])

  const config = React.useMemo(() => asTypedConfig(selectedTemplate?.templateConfig), [selectedTemplate])
  /* Stan zasilania czytamy z mapowania szablonu (typ, indeks, bit), a nie z `deviceStatus.isWorking`:
     tamta flaga powstaje z ramki o nazwie `workingStatus` wpisanej na sztywno na potrzeby dashboardu,
     więc dla urządzeń mapujących status inaczej dawała stałe `off` i wykres live stał w luce.

     Bez `useMemo` celowo: `getMqttValue` porównuje znacznik ramki z `Date.now()`, więc wynik zmienia
     się z upływem czasu, a nie tylko przy nowych ramkach. Zapamiętany po `mqtt` zostawał na stanie
     sprzed progu bezczynności — po 180 s telemetria przechodziła w `stale`, layout się przerysowywał,
     ale `telemetry.frames` zachowuje tożsamość, więc memo zwracało starą wartość i wskaźnik nigdy
     nie gasł. */
  const powerState = devicePowerStateOf(selectedTemplate, mqtt)

  /* Zbieranie historii serii live siedzi w layoucie, a nie na stronie urządzenia. Layout obejmuje
     wszystkie podstrony i nie odmontowuje się przy przejściu na tabelę alarmów, więc próbki, luki
     i przedziały alarmów przeżywają nawigację. Umieszczone na stronie ginęły razem z nią i wykres
     po powrocie zaczynał od pustej serii. */
  const liveHistory = useDeviceLiveHistory(mqtt, powerState, telemetryFreshness, deviceStatus?.alertStatus ?? 'normal')

  const loadDevice = React.useCallback(() => {
    if (!id || !canAccessDevice) return

    void dispatch(getDeviceData(id))
      .unwrap()
      .then(() => {
        setVerifiedDeviceIds((ids) => withDeviceId(ids, id, true))
        setUnavailableDeviceIds((ids) => withDeviceId(ids, id, false))
      })
      .catch((message: unknown) => {
        // Odrzucony thunk pokazuje globalny errorLoggingMiddleware; widok renderuje ErrorState albo stan niedostępności.
        setUnavailableDeviceIds((ids) => withDeviceId(ids, id, message === DEVICE_NOT_FOUND_ERROR))
      })
  }, [canAccessDevice, dispatch, id])

  React.useEffect(() => {
    if (device?.id === id && device?.deviceTemplates) return
    if (deviceLoadStatus === 'loading' || deviceLoadStatus === 'failed') return

    loadDevice()
  }, [device?.deviceTemplates, device?.id, deviceLoadStatus, id, loadDevice])

  // Dostęp serwisanta zależy od bieżącego przypisania — wejście na kartę sprawdza go ponownie, także przy danych w pamięci.
  const isDeviceCached = device?.id === id && Boolean(device?.deviceTemplates)
  React.useEffect(() => {
    if (!id || retriedDeviceId === id) return
    setRetriedDeviceId(id)
    if (deviceLoadStatus === 'failed' || (isServiceStaffOnly && isDeviceCached)) loadDevice()
  }, [deviceLoadStatus, id, isDeviceCached, isServiceStaffOnly, loadDevice, retriedDeviceId])

  const loadTemplate = React.useCallback(() => {
    if (!canAccessDevice || !device?.id) return

    const requestKey = `${device.id}:${selectedLanguage}`
    if (requestedTemplatesRef.current.has(requestKey)) return

    requestedTemplatesRef.current.add(requestKey)
    setFailedTemplateRequestKey((current) => (current === requestKey ? null : current))

    void dispatch(getTemplateForDeviceData({ deviceId: device.id, language: selectedLanguage }))
      .unwrap()
      .catch(() => {
        requestedTemplatesRef.current.delete(requestKey)
        setFailedTemplateRequestKey(requestKey)
        // Odrzucony thunk pokazuje globalny errorLoggingMiddleware; widok renderuje ErrorState.
      })
  }, [canAccessDevice, device?.id, dispatch, selectedLanguage])

  React.useEffect(() => {
    if (!canAccessDevice) return
    if (!device?.id) return

    const hasTemplateForLanguage = Object.values(allTemplatesData).some(
      (template) =>
        device.deviceTemplates?.includes(template.id) &&
        template.language?.toUpperCase?.() === selectedLanguage &&
        template.templateConfig,
    )
    if (hasTemplateForLanguage) return

    loadTemplate()
  }, [allTemplatesData, canAccessDevice, device?.deviceTemplates, device?.id, loadTemplate, selectedLanguage])

  if (!id) {
    return <Navigate to='/dashboard' replace />
  }

  if (!profile && (profileStatus === 'idle' || profileStatus === 'loading')) {
    return <LoadingState className='flex-1' />
  }

  if (!profile || profileStatus === 'failed' || !canAccessDevice) {
    return <Navigate to='/dashboard' replace />
  }

  if (!device || device.id !== id || (isServiceStaffOnly && !verifiedDeviceIds.has(id))) {
    if (deviceLoadStatus !== 'failed' || retriedDeviceId !== id) return <LoadingState className='flex-1' />

    return unavailableDeviceIds.has(id) ? (
      <EmptyState
        title={t('device.unavailableTitle')}
        hint={t('device.unavailableHint')}
        className='flex-1 justify-center'
      />
    ) : (
      <ErrorState className='flex-1 justify-center' onRetry={loadDevice} />
    )
  }

  const templateRequestFailed = failedTemplateRequestKey === currentTemplateRequestKey && !selectedTemplate
  const templateRequestPending =
    !selectedTemplate &&
    currentTemplateRequestKey !== null &&
    requestedTemplatesRef.current.has(currentTemplateRequestKey) &&
    templateLoadStatus === 'loading'
  const templateMissing =
    !selectedTemplate &&
    !templateRequestFailed &&
    !templateRequestPending &&
    currentTemplateRequestKey !== null &&
    requestedTemplatesRef.current.has(currentTemplateRequestKey) &&
    templateLoadStatus === 'succeeded'

  const base = `/device/${device.id}`
  const isServiceRequestsPath = pathname.startsWith(`${base}/service-requests`)

  if (isServiceStaffOnly && pathname.startsWith(`${base}/technical-inspection`)) {
    return <Navigate to={base} replace />
  }

  const outlet = (
    <React.Suspense fallback={<LoadingState className='flex-1' />}>
      <Outlet
        context={{
          deviceMeasurementsHistory,
          device,
          id,
          selectedTemplate,
          deviceStatus,
          config,
          mqtt,
          powerState,
          telemetryFreshness,
          liveHistory,
        }}
      />
    </React.Suspense>
  )

  const tabs: NavTabItem[] = [
    { to: base, label: t('common.main'), match: (path) => path === base },
    {
      to: `${base}/alarms`,
      label: t('device.alarmsAndErrors'),
      match: (path) => path.startsWith(`${base}/alarms`),
    },
    ...(isServiceStaffOnly
      ? []
      : [
          {
            to: `${base}/technical-inspection-history`,
            label: t('device.inspectionHistory'),
            match: (path: string) =>
              path.startsWith(`${base}/technical-inspection-history`) || path.endsWith('/technical-inspection'),
          },
        ]),
    { to: `${base}/help`, label: t('device.documentation'), match: (path) => path.startsWith(`${base}/help`) },
    ...(canRequestService
      ? [
          {
            to: `${base}/service-requests`,
            label: t('device.serviceRequests.tab'),
            match: (path: string) => path.startsWith(`${base}/service-requests`),
          },
        ]
      : []),
  ]

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-3.5'>
      <div className='flex flex-wrap items-center gap-x-3.5 gap-y-2'>
        <span className='flex size-10 shrink-0 items-center justify-center rounded-crane-sm bg-crane-card-inset text-crane-muted-foreground'>
          <DeviceTypeIcon className='size-crane-icon-tile' />
        </span>
        <div className='min-w-0 flex-1 md:flex-none'>
          <div className='truncate text-crane-2xl font-semibold leading-5 text-crane-foreground'>{device.name}</div>
          <div className='text-xs text-crane-muted-foreground'>
            {t('device.serialNumber')}: {device.serialNumber} · {t('device.udtNumber')}: {device.udtNumber}
          </div>
        </div>
        <div className='flex w-full flex-wrap items-center gap-2 md:contents'>
          <DevicePowerChip state={powerState} />
          <StatusChip alertStatus={alertStatusOf(deviceStatus)} variant='solid' withIcon />
          {canRequestService && (
            <Button size='sm' className='ml-auto h-10 md:h-8' onClick={() => navigate(`${base}/service-requests/new`)}>
              <Wrench aria-hidden className='size-4' />
              {t('device.serviceRequests.report')}
            </Button>
          )}
        </div>
      </div>

      <div className='min-w-0 max-w-full overflow-x-auto'>
        <NavTabBar items={tabs} pathname={pathname} />
      </div>

      <Separator />

      {/* Zgłoszenie serwisu nie zależy od szablonu pomiarów — działa także na urządzeniu bez szablonu. */}
      {isServiceRequestsPath ? (
        outlet
      ) : templateRequestFailed ? (
        <ErrorState className='flex-1 justify-center' onRetry={loadTemplate} />
      ) : templateMissing ? (
        <EmptyState
          title={t('device.noTemplateAssigned')}
          hint={t('device.emptyTemplateHint')}
          className='flex-1 justify-center'
        />
      ) : templateRequestPending || !selectedTemplate ? (
        <LoadingState className='flex-1' />
      ) : (
        outlet
      )}
    </div>
  )
}

export default DeviceLayout
